import React from "react";
import { connect } from "react-redux";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { useLocation } from "react-router-dom";
import { clearError } from "../../Redux/ErrorBoundary/ErrorBoundaryActions";
import { RootState } from "../../Redux/Reducers/RootReducer";
import { AppError } from "../../Types/AppError";
import STRINGS from "../../Constants/Strings";

type ErrorPopupProps = {
  error: AppError | null;
  clearError: () => void;
};

const ErrorPopup: React.FC<ErrorPopupProps> = ({ error, clearError }) => {
  const location = useLocation();

  const isEmbeddingMode = (() => {
    const searchParams = new URLSearchParams(location.search);
    const embeddingParam = searchParams.get("embedding") || searchParams.get("embedded");
    const isInIframe = (() => {
      try { return window.self !== window.top; } catch { return true; }
    })();
    return (
      location.pathname.toLowerCase().includes("/embedding") ||
      embeddingParam === "true" ||
      embeddingParam === "1" ||
      isInIframe
    );
  })();

  const isValidGuid = (guid: string | undefined | null) => {
    if (!guid) return false;
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
      guid,
    );
  };

  const handleReload = () => {
    const path = location?.pathname || "/";
    const segments = path.split("/").filter(Boolean);

    const isChatRoute =
      segments[0] === "chat" &&
      segments.length >= 2 &&
      isValidGuid(segments[1]);

    const isSkillsRoute =
      segments[0] === "skills" ||
      segments[0] === "skill" ||
      segments[0] === "chatbot" ||
      segments[0] === "app";

    // A complete skill route has code and conversationId: /skill/:code/:conversationId or /chatbot/:code/:conversationId or /app/:code/:conversationId
    const isCompleteSkillRoute =
      (segments[0] === "skill" || segments[0] === "chatbot" || segments[0] === "app") &&
      segments.length >= 3 &&
      isValidGuid(segments[2]);

    if (isCompleteSkillRoute) {
      const tech = (error?.technicalDetails || "").toString().toLowerCase();
      const msg = (error?.message || "").toString().toLowerCase();
      if (
        tech.includes("malformed skill url") ||
        tech.includes("unable to load skill") ||
        msg.includes("invalid url")
      ) {
        // Redirect back to the skills listing when a skill route fails to load
        clearError();
        window.location.assign("/skills");
        return;
      }
    }

    let target: string;

    // Check if this was a first conversation error
    if (error?.isFirstConversationError) {
      target = "/";
    } else if (isCompleteSkillRoute) {
      // Stay on the current skill page (e.g. /skill/code/conversationId)
      target = path;
    } else if (isSkillsRoute) {
      // Broken/incomplete skill route — go back to skills listing
      target = "/skills";
    } else if (isChatRoute) {
      // For subsequent conversation errors, stay on current conversation
      target = `/chat/${segments[1]}`;
    } else {
      // For other routes, try to restore last conversation or go home
      const lastGuid = (() => {
        try {
          return localStorage.getItem("lastConversationId");
        } catch {
          return null;
        }
      })();

      if (lastGuid && isValidGuid(lastGuid)) {
        target = `/chat/${lastGuid}`;
      } else {
        target = "/";
      }
    }

    clearError();
    window.location.assign(target);
  };

  return (
    <Dialog
      visible={!!error}
      onHide={clearError}
      header={STRINGS.ERRORPOPUP.HEADER}
      closable={false}
      className={`error-dialog${isEmbeddingMode ? " error-dialog--embedded" : ""}`}
    >
      <div className="p-mb">
        <p className="text-lg font-medium">
          {error?.message || STRINGS.ERRORPOPUP.OOPS}
        </p>

        <div>
          <p>{STRINGS.ERRORPOPUP.TECHNICAL}</p>
          <InputTextarea
            readOnly
            value={
              error?.technicalDetails || "No additional details available."
            }
            style={{ width: "100%" }}
            className="text-sm error-textarea"
            autoResize
            rows={5}
            cols={30}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <Button
          outlined
          label="Copy"
          className="rounded me-2"
          onClick={() =>
            navigator.clipboard.writeText(
              (error?.technicalDetails ?? "N/A").toString(),
            )
          }
        />
        <Button
          label="Reload Page"
          className="rounded"
          onClick={handleReload}
          style={{ width: "140px" }}
        />
      </div>
    </Dialog>
  );
};

const mapStateToProps = (state: RootState) => ({
  error: state.Error.error,
});

export default connect(mapStateToProps, { clearError })(ErrorPopup);
